import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FileDownload,
  CheckCircle,
  Description,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api, PageHeader, PageContent, useNotification } from '@viilutalous/common';

export default function ExportData({ tenantId }) {
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      const response = await api.get(`/${tenantId}/export/download`, {
        responseType: 'blob',
      });

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `tenant_export_${tenantId}.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(t('export.success'));
    } catch (err) {
      console.error('Export error:', err);
      const errorMessage = err.response?.data?.detail || t('export.error');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setExporting(false);
    }
  };

  const exportFeatures = [
    'export.feature_customers',
    'export.feature_invoices',
    'export.feature_products',
    'export.feature_vouchers',
    'export.feature_settings',
  ];

  return (
    <>
      <PageHeader />
      <PageContent>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ mb: 3 }}>
            {t('export.title')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Description sx={{ fontSize: 48, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {t('export.description_title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('export.description')}
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t('export.includes')}
            </Typography>

            <List dense>
              {exportFeatures.map((feature) => (
                <ListItem key={feature}>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary={t(feature)} />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
                onClick={handleExport}
                disabled={exporting || !tenantId}
              >
                {exporting ? t('export.exporting') : t('export.download_button')}
              </Button>
            </Box>

            {!tenantId && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('export.select_tenant')}
              </Alert>
            )}
          </Paper>
        </Container>
      </PageContent>
    </>
  );
}
